import React, { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, AlertTriangle, Eye, Users } from 'lucide-react';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyStatCard,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminBehaviorService, { BehaviorOverview, BehaviorRange } from '../../../services/api/adminBehaviorService';
import { DEVICE_LABELS, formatMs, formatNumber, formatPercent } from './format';

const DEVICE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

interface Props {
  range: BehaviorRange;
}

const OverviewTab: React.FC<Props> = ({ range }) => {
  const [data, setData] = useState<BehaviorOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminBehaviorService
      .getOverview(range)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError('概要の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <TaskySpinner />
      </div>
    );
  }
  if (error) {
    return <TaskyErrorState title={error} />;
  }
  if (!data || data.totals.page_views === 0) {
    return <TaskyEmptyState title="この期間の行動データはまだありません" description="計測が始まると、ここにセッション数やページ別の滞在時間が表示されます" />;
  }

  const { totals } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <TaskyStatCard label="セッション" value={formatNumber(totals.sessions)} icon={<Activity />} iconTone="primary" />
        <TaskyStatCard
          label="ユーザー"
          value={formatNumber(totals.users)}
          delta={`匿名 ${formatNumber(totals.anonymous)}`}
          deltaTone="neutral"
          icon={<Users />}
          iconTone="success"
        />
        <TaskyStatCard
          label="ページビュー"
          value={formatNumber(totals.page_views)}
          delta={`クリック ${formatNumber(totals.clicks)}`}
          deltaTone="neutral"
          icon={<Eye />}
          iconTone="purple"
        />
        <TaskyStatCard
          label="エラー"
          value={formatNumber(totals.api_errors + totals.js_errors)}
          delta={`API ${formatNumber(totals.api_errors)} / JS ${formatNumber(totals.js_errors)}`}
          deltaTone={totals.api_errors + totals.js_errors > 0 ? 'down' : 'neutral'}
          icon={<AlertTriangle />}
          iconTone="warning"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-tasky-md border border-warm-border p-4 lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold text-dark">日別推移</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sessions" name="セッション" stroke="#0088FE" dot={false} />
                <Line type="monotone" dataKey="page_views" name="PV" stroke="#00C49F" dot={false} />
                <Line type="monotone" dataKey="api_errors" name="APIエラー" stroke="#FF8042" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-tasky-md border border-warm-border p-4">
          <h3 className="mb-3 text-sm font-semibold text-dark">デバイス</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.devices.map((d) => ({ name: DEVICE_LABELS[d.device_category] ?? d.device_category, value: d.sessions }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                  label
                >
                  {data.devices.map((d, index) => (
                    <Cell key={d.device_category} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-dark">上位ページ</h3>
        <div className="overflow-x-auto">
          <TaskyTable className="min-w-[720px]">
            <TaskyTableHead>
              <TaskyTableRow>
                <TaskyTableHeaderCell>ページ</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>PV</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>ユーザー</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>滞在（中央値）</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>離脱率</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>APIエラー率</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {data.top_pages.map((page) => (
                <TaskyTableRow key={page.page_pattern}>
                  <TaskyTableCell className="font-mono text-xs">{page.page_pattern}</TaskyTableCell>
                  <TaskyTableCell>{formatNumber(page.views)}</TaskyTableCell>
                  <TaskyTableCell>{formatNumber(page.unique_users)}</TaskyTableCell>
                  <TaskyTableCell>{formatMs(page.median_duration_ms)}</TaskyTableCell>
                  <TaskyTableCell>{formatPercent(page.exit_rate)}</TaskyTableCell>
                  <TaskyTableCell>{formatPercent(page.api_error_rate)}</TaskyTableCell>
                </TaskyTableRow>
              ))}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
